import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  apiService,
  DashboardCrawlOptionsPayload,
  DashboardCrawlStatus,
  DashboardKnowledgeEntry,
  DashboardSiteProfile,
} from '@/api/apiService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useDashboardSite } from '@/pages/dashboard/DashboardSiteContext';
import { DashboardHowItWorksLink } from '@/pages/dashboard/DashboardHelpPage';
import {
  crawlStartFailureMessage,
  withCrawlStartRetries,
} from '@/utils/crawlStartRetries';

const DEFAULT_CRAWL: DashboardCrawlOptionsPayload = {
  crawl_type: 'linked',
  max_pages: 25,
  follow_external_links: false,
  include_patterns: [],
  exclude_patterns: [],
  follow_redirects: true,
  max_depth: 3,
};

function textToPatterns(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

type CrawlFormState = DashboardCrawlOptionsPayload & {
  includeText: string;
  excludeText: string;
};

function CrawlOptionsFields({
  value,
  onChange,
  disabled,
}: {
  value: CrawlFormState;
  onChange: (next: CrawlFormState) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="crawl-type">Crawl type</Label>
        <select
          id="crawl-type"
          className="w-full rounded-md border border-input bg-background p-2 text-sm"
          value={value.crawl_type}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              crawl_type: e.target.value as DashboardCrawlOptionsPayload['crawl_type'],
            })
          }
        >
          <option value="url">URL only — crawls just the specified URL</option>
          <option value="linked">URL + linked pages — crawls the URL and pages it links to</option>
          <option value="sitemap">Deep crawl — uses sitemap.xml to find and crawl pages</option>
        </select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="max-pages">Max pages</Label>
          <select
            id="max-pages"
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            value={value.max_pages}
            disabled={disabled || value.crawl_type === 'url'}
            onChange={(e) => onChange({ ...value, max_pages: Number(e.target.value) })}
          >
            {[1, 5, 10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} page{n === 1 ? '' : 's'}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">Server may cap this further to control cost.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="max-depth">Max crawl depth (1–10)</Label>
          <Input
            id="max-depth"
            type="number"
            min={1}
            max={10}
            value={value.max_depth}
            disabled={disabled || value.crawl_type === 'url'}
            onChange={(e) =>
              onChange({ ...value, max_depth: Math.min(10, Math.max(1, Number(e.target.value) || 1)) })
            }
          />
          <p className="text-xs text-muted-foreground">Higher values crawl more pages but take longer.</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
        <div>
          <div className="text-sm font-medium">Follow external links</div>
          <p className="text-xs text-muted-foreground">If enabled, follow links to other domains.</p>
        </div>
        <Switch
          checked={value.follow_external_links}
          disabled={disabled || value.crawl_type === 'url'}
          onCheckedChange={(checked) => onChange({ ...value, follow_external_links: checked })}
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
        <div>
          <div className="text-sm font-medium">Follow redirects</div>
          <p className="text-xs text-muted-foreground">If enabled, follow HTTP redirects.</p>
        </div>
        <Switch
          checked={value.follow_redirects}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ ...value, follow_redirects: checked })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="include-patterns">Include URL patterns (optional)</Label>
        <Input
          id="include-patterns"
          value={value.includeText}
          disabled={disabled}
          placeholder="*/pricing*, */about*"
          onChange={(e) => onChange({ ...value, includeText: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          Leave empty to crawl any page the crawler finds (within your other limits).
          If you add patterns, <strong>only URLs that match at least one pattern</strong> are crawled —
          other pages are skipped. Your starting URL is always crawled. Comma-separated; use * as a wildcard.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="exclude-patterns">Exclude URL patterns (optional)</Label>
        <Input
          id="exclude-patterns"
          value={value.excludeText}
          disabled={disabled}
          placeholder="*/blog*, */tag/*, */cart*"
          onChange={(e) => onChange({ ...value, excludeText: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          URLs matching these patterns are never crawled (even if they match an include pattern).
          Comma-separated; use * as a wildcard.
        </p>
      </div>
    </div>
  );
}

function toPayload(form: CrawlFormState): DashboardCrawlOptionsPayload {
  return {
    crawl_type: form.crawl_type,
    max_pages: form.crawl_type === 'url' ? 1 : form.max_pages,
    follow_external_links: form.crawl_type === 'url' ? false : form.follow_external_links,
    include_patterns: textToPatterns(form.includeText),
    exclude_patterns: textToPatterns(form.excludeText),
    follow_redirects: form.follow_redirects,
    max_depth: form.crawl_type === 'url' ? 1 : form.max_depth,
  };
}

export default function DashboardContentPage() {
  const {
    selectedSiteId,
    selectedSite,
    sites,
    canEdit,
    billing,
    isPlatformAdmin,
    reloadSites,
  } = useDashboardSite();

  const [profile, setProfile] = useState<DashboardSiteProfile | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [crawlStatus, setCrawlStatus] = useState<DashboardCrawlStatus | null>(null);
  const [crawlBusy, setCrawlBusy] = useState(false);
  const [confirmCrawl, setConfirmCrawl] = useState(false);
  /** Short inline status near start/claim (e.g. Starting… / Retrying 2/3…). */
  const [crawlerWarmStatus, setCrawlerWarmStatus] = useState<string | null>(null);
  const [crawlForm, setCrawlForm] = useState<CrawlFormState>({
    ...DEFAULT_CRAWL,
    includeText: '',
    excludeText: '',
  });

  const [claimUrl, setClaimUrl] = useState('');
  const [claimName, setClaimName] = useState('');
  const [claimDescription, setClaimDescription] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);

  const [entries, setEntries] = useState<DashboardKnowledgeEntry[]>([]);
  const [faqOpen, setFaqOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<DashboardKnowledgeEntry | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [faqBusy, setFaqBusy] = useState(false);

  const hasNoSites = sites.length === 0;
  const canClaim = hasNoSites && !isPlatformAdmin;

  const crawlBlockedByBilling =
    Boolean(billing?.requires_prepaid_balance) &&
    (Boolean(billing?.payment_required) || Number(billing?.balance_usd || 0) <= 0);

  const loadAll = async (siteId: number) => {
    try {
      const [site, knowledge, status] = await Promise.all([
        apiService.dashboardGetSite(siteId),
        apiService.dashboardListKnowledge(siteId),
        apiService.dashboardGetCrawlStatus(siteId),
      ]);
      setProfile(site);
      setName(site.name || '');
      setDescription(site.description || '');
      setEntries(knowledge.entries || []);
      setCrawlStatus(status);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load site content');
    }
  };

  useEffect(() => {
    if (!selectedSiteId) {
      setProfile(null);
      setEntries([]);
      setCrawlStatus(null);
      return;
    }
    void loadAll(selectedSiteId);
  }, [selectedSiteId]);

  useEffect(() => {
    if (!selectedSiteId || !crawlStatus?.crawl_in_progress) return;
    const timer = setInterval(() => {
      void apiService
        .dashboardGetCrawlStatus(selectedSiteId)
        .then(setCrawlStatus)
        .catch(() => undefined);
    }, 10000);
    return () => clearInterval(timer);
  }, [selectedSiteId, crawlStatus?.crawl_in_progress]);

  const handleSaveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedSiteId || !canEdit) return;
    setSavingProfile(true);
    try {
      const updated = await apiService.dashboardUpdateSite(selectedSiteId, {
        name: name.trim(),
        description: description.trim(),
      });
      setProfile(updated);
      toast.success('Site info saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save site info');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleConfirmCrawl = async () => {
    if (!selectedSiteId || !canEdit) return;
    setCrawlBusy(true);
    setCrawlerWarmStatus('Starting…');
    try {
      await withCrawlStartRetries(
        () => apiService.dashboardStartCrawl(selectedSiteId, toPayload(crawlForm)),
        {
          onRetry: (attempt, maxAttempts) => {
            setCrawlerWarmStatus(`Retrying ${attempt}/${maxAttempts}…`);
          },
        },
      );
      toast.success('Crawl started');
      const status = await apiService.dashboardGetCrawlStatus(selectedSiteId);
      setCrawlStatus(status);
      setConfirmCrawl(false);
    } catch (error: unknown) {
      toast.error(crawlStartFailureMessage(error, 'Failed to start crawl'));
    } finally {
      setCrawlBusy(false);
      setCrawlerWarmStatus(null);
    }
  };

  const handleClaim = async (event: FormEvent) => {
    event.preventDefault();
    if (!canClaim) return;
    setClaimBusy(true);
    setCrawlerWarmStatus('Starting…');
    try {
      const result = await withCrawlStartRetries(
        () =>
          apiService.dashboardClaimSite({
            url: claimUrl.trim(),
            site_name: claimName.trim() || undefined,
            site_description: claimDescription.trim() || undefined,
            ...toPayload(crawlForm),
          }),
        {
          onRetry: (attempt, maxAttempts) => {
            setCrawlerWarmStatus(`Retrying ${attempt}/${maxAttempts}…`);
          },
        },
      );
      toast.success(result.message || 'Website claimed and crawl started');
      await reloadSites(result.site_id);
    } catch (error: unknown) {
      toast.error(crawlStartFailureMessage(error, 'Failed to claim website'));
    } finally {
      setClaimBusy(false);
      setCrawlerWarmStatus(null);
    }
  };

  const openCreateFaq = () => {
    setEditingEntry(null);
    setQuestion('');
    setAnswer('');
    setFaqOpen(true);
  };

  const openEditFaq = (entry: DashboardKnowledgeEntry) => {
    setEditingEntry(entry);
    setQuestion(entry.question || '');
    setAnswer(entry.answer || '');
    setFaqOpen(true);
  };

  const handleSaveFaq = async () => {
    if (!selectedSiteId || !canEdit) return;
    setFaqBusy(true);
    try {
      if (editingEntry?.entry_id) {
        await apiService.dashboardUpdateKnowledge(selectedSiteId, editingEntry.entry_id, {
          question: question.trim(),
          answer: answer.trim(),
        });
        toast.success('FAQ updated');
      } else {
        await apiService.dashboardCreateKnowledge(selectedSiteId, {
          question: question.trim(),
          answer: answer.trim(),
        });
        toast.success('FAQ added');
      }
      const knowledge = await apiService.dashboardListKnowledge(selectedSiteId);
      setEntries(knowledge.entries || []);
      setFaqOpen(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save FAQ');
    } finally {
      setFaqBusy(false);
    }
  };

  const handleDeleteFaq = async (entry: DashboardKnowledgeEntry) => {
    if (!selectedSiteId || !canEdit) return;
    if (!window.confirm(`Delete FAQ “${entry.question}”?`)) return;
    try {
      await apiService.dashboardDeleteKnowledge(selectedSiteId, entry.entry_id);
      setEntries((prev) => prev.filter((item) => item.entry_id !== entry.entry_id));
      toast.success('FAQ deleted');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to delete FAQ');
    }
  };

  if (canClaim) {
    const balanceUsd = Number(billing?.balance_usd || 0);
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            PLEASE CRAWL YOUR WEBSITE TO CONTINUE
          </h2>
          <p className="text-sm text-muted-foreground">
            Claim your domain once, then crawl it. Each account may have only one website.{' '}
            <DashboardHowItWorksLink hash="get-started" />
          </p>
          <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
            <div>
              Current balance:{' '}
              <span className="font-semibold">
                {new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                }).format(balanceUsd)}
              </span>
            </div>
            {crawlBlockedByBilling ? (
              <p className="mt-2 text-amber-700 dark:text-amber-300">
                Go to{' '}
                <Link to="/dashboard/billing" className="font-medium underline underline-offset-2">
                  Billing
                </Link>{' '}
                and make a deposit if you want to continue with setup. Crawling stays disabled until
                your prepaid balance is positive.
              </p>
            ) : (
              <p className="mt-2 text-muted-foreground">
                Balance looks good — enter your website below to claim and start the first crawl.
                Funds will move to your site after claim.
              </p>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Claim &amp; first crawl</CardTitle>
            <CardDescription>
              After this, the URL is locked. You can only recrawl the same site from Content.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleClaim}>
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                One website per account. Choose carefully — you cannot switch domains later from the dashboard.
              </div>
              <div className="space-y-2">
                <Label htmlFor="claim-url">Website URL</Label>
                <Input
                  id="claim-url"
                  type="url"
                  required
                  placeholder="https://example.com"
                  value={claimUrl}
                  onChange={(e) => setClaimUrl(e.target.value)}
                  disabled={claimBusy || crawlBlockedByBilling}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="claim-name">Site name (optional)</Label>
                <Input
                  id="claim-name"
                  value={claimName}
                  onChange={(e) => setClaimName(e.target.value)}
                  disabled={claimBusy || crawlBlockedByBilling}
                  maxLength={255}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="claim-description">Description (optional)</Label>
                <Textarea
                  id="claim-description"
                  value={claimDescription}
                  onChange={(e) => setClaimDescription(e.target.value)}
                  disabled={claimBusy || crawlBlockedByBilling}
                  rows={2}
                  maxLength={2000}
                />
              </div>

              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
                Crawling uses tokens from your prepaid balance. Cost depends on site size and max pages.
              </div>

              {crawlBlockedByBilling ? (
                <p className="text-sm text-destructive">
                  Add funds in{' '}
                  <Link to="/dashboard/billing" className="underline underline-offset-2">
                    Billing
                  </Link>{' '}
                  before crawling.
                </p>
              ) : null}

              <CrawlOptionsFields
                value={crawlForm}
                onChange={setCrawlForm}
                disabled={claimBusy || crawlBlockedByBilling}
              />

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" disabled={claimBusy || crawlBlockedByBilling || !claimUrl.trim()}>
                  {claimBusy ? 'Starting crawler…' : 'Claim website & start crawl'}
                </Button>
                {crawlerWarmStatus ? (
                  <span className="text-sm text-muted-foreground" aria-live="polite">
                    {crawlerWarmStatus}
                  </span>
                ) : null}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!selectedSiteId) {
    return (
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Content</h2>
        <p className="text-sm text-muted-foreground">
          {isPlatformAdmin
            ? 'Select a site to manage content, or use admin crawl tools to create sites.'
            : 'Select a site to manage content.'}{' '}
          <DashboardHowItWorksLink hash="pages" />
        </p>
      </div>
    );
  }

  const pageCount = crawlStatus?.page_count ?? profile?.page_count ?? 0;
  const chunkCount = crawlStatus?.chunk_count ?? 0;
  const crawlInProgress = Boolean(crawlStatus?.crawl_in_progress);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Content</h2>
        <p className="text-sm text-muted-foreground">
          Site info, crawl refresh, and FAQ facts for {selectedSite?.name || 'this site'}.
          {!isPlatformAdmin ? ' One website per account — URL is fixed after claim.' : ''}{' '}
          <DashboardHowItWorksLink hash="pages" />
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Crawl summary</CardTitle>
          <CardDescription>What the assistant can search right now.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 text-sm">
          <span className="rounded-md border border-border px-2 py-1">
            Pages indexed: <strong>{pageCount}</strong>
          </span>
          <span className="rounded-md border border-border px-2 py-1">
            Chunks: <strong>{chunkCount}</strong>
          </span>
          <span className="rounded-md border border-border px-2 py-1">
            Status: <strong>{crawlInProgress ? 'Crawling…' : 'Idle'}</strong>
          </span>
          {crawlStatus?.last_crawl_started_at ? (
            <span className="rounded-md border border-border px-2 py-1 text-muted-foreground">
              Last started {new Date(crawlStatus.last_crawl_started_at).toLocaleString()}
            </span>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Site info</CardTitle>
          <CardDescription>Name and description. Website URL cannot be changed after claim.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSaveProfile}>
            <div className="space-y-2">
              <Label htmlFor="site-url">Website URL</Label>
              <Input id="site-url" value={profile?.url || selectedSite?.url || ''} disabled readOnly />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-name">Name</Label>
              <Input
                id="site-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit || savingProfile}
                maxLength={255}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-description">Description</Label>
              <Textarea
                id="site-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canEdit || savingProfile}
                maxLength={2000}
                rows={4}
              />
            </div>
            {canEdit ? (
              <Button type="submit" disabled={savingProfile}>
                {savingProfile ? 'Saving…' : 'Save site info'}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">Viewers cannot edit site info.</p>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recrawl content</CardTitle>
          <CardDescription>
            Refresh pages from your locked website URL so the assistant stays up to date.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
            Crawling uses tokens from your prepaid balance. Cost depends on site size and max pages.
          </div>
          {crawlBlockedByBilling ? (
            <p className="text-sm text-destructive">Add funds in Billing before starting a crawl.</p>
          ) : null}
          <CrawlOptionsFields
            value={crawlForm}
            onChange={setCrawlForm}
            disabled={!canEdit || crawlBusy || crawlInProgress}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={!canEdit || crawlBusy || crawlInProgress || crawlBlockedByBilling}
              onClick={() => setConfirmCrawl(true)}
            >
              Start crawl
            </Button>
            {crawlerWarmStatus ? (
              <span className="text-sm text-muted-foreground" aria-live="polite">
                {crawlerWarmStatus}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle>FAQ &amp; facts</CardTitle>
            <CardDescription>
              Add facts that aren&apos;t on your website. The bot searches these like crawled pages.
            </CardDescription>
          </div>
          {canEdit ? (
            <Button type="button" size="sm" onClick={openCreateFaq}>
              Add FAQ
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No FAQ entries yet.</p>
          ) : (
            <ul className="space-y-3">
              {entries.map((entry) => (
                <li key={entry.entry_id} className="rounded-md border border-border p-3">
                  <div className="text-sm font-medium">{entry.question}</div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{entry.answer}</div>
                  {canEdit ? (
                    <div className="mt-2 flex gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => openEditFaq(entry)}>
                        Edit
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => void handleDeleteFaq(entry)}>
                        Delete
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmCrawl} onOpenChange={setConfirmCrawl}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start crawl?</DialogTitle>
            <DialogDescription>
              Crawling uses tokens from your prepaid balance. This recrawls{' '}
              <strong>{profile?.url || selectedSite?.url}</strong> with type{' '}
              <strong>{crawlForm.crawl_type}</strong>
              {crawlForm.crawl_type !== 'url' ? ` (max ${crawlForm.max_pages} pages)` : ''}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
            {crawlerWarmStatus ? (
              <span className="text-sm text-muted-foreground sm:mr-auto" aria-live="polite">
                {crawlerWarmStatus}
              </span>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setConfirmCrawl(false)} disabled={crawlBusy}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleConfirmCrawl()} disabled={crawlBusy}>
              {crawlBusy ? 'Starting crawler…' : 'Start crawl'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={faqOpen} onOpenChange={setFaqOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            <DialogDescription>Question and answer only — facts the assistant can retrieve.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="faq-question">Question</Label>
              <Input
                id="faq-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={500}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="faq-answer">Answer</Label>
              <Textarea
                id="faq-answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                maxLength={5000}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFaqOpen(false)} disabled={faqBusy}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveFaq()}
              disabled={faqBusy || question.trim().length < 3 || answer.trim().length < 10}
            >
              {faqBusy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
